import React, { useState, useRef, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Typography,
    Button,
    IconButton,
    TextField,
    Paper,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Modal,
    Fade,
    Backdrop,
    Divider,
    Alert,
    Grid,
} from "@mui/material";
import { Icon } from "../../icons";
import { COLORS, TYPOGRAPHY } from "../../constants/color";
import useFormPersistence from "../../hooks/useFormPersistence";

const SHAPE_TYPES = {
    RECTANGLE: 'rectangle',
    CIRCLE: 'circle',
    ARROW: 'arrow',
    LINE: 'line',
    TEXT: 'text',
   // SQUARE: 'square',
    //FREEHAND: 'freehand',
};

const PhotoUploadWithDrawing = ({ open, onClose, onSave, maxPhotos = 6, persistenceKey = null }) => {
    // Draft state - Consolidated into persistence hook if key provided
    const [draftData, setDraftData, clearDraft] = useFormPersistence(
        persistenceKey,
        {
            title: "",
            remarks: "", // For compatibility with other forms if needed
            shapes: [],
            preview: null,
        }
    );

    const {
        title = "",
        shapes = [],
        preview = null,
    } = draftData || {};

    const updateDraft = (updates) => {
        setDraftData(prev => ({ ...prev, ...updates }));
    };

    const setTitle = (val) => updateDraft({ title: val });
    const setShapes = (val) => updateDraft({ shapes: val });
    const setPreview = (val) => updateDraft({ preview: val });

    const [selectedFile, setSelectedFile] = useState(null);
    const [shapeHistory, setShapeHistory] = useState([[]]);
    const [historyIndex, setHistoryIndex] = useState(0);
    const [currentShape, setCurrentShape] = useState(null);
    const [drawingMode, setDrawingMode] = useState(SHAPE_TYPES.RECTANGLE);
    const [brushSize, setBrushSize] = useState(3);
    const [brushColor, setBrushColor] = useState('#ff0000');
    const [isDrawing, setIsDrawing] = useState(false);
    const [textInput, setTextInput] = useState("");
    const [showTextModal, setShowTextModal] = useState(false);
    const [textPosition, setTextPosition] = useState({ x: 0, y: 0 });
    const [freehandPoints, setFreehandPoints] = useState([]);
    const [showCamera, setShowCamera] = useState(false);
    const [error, setError] = useState("");
    
    const canvasRef = useRef(null);
    const cameraRef = useRef(null);
    const startPosRef = useRef({ x: 0, y: 0 });

    // Initialize camera
    useEffect(() => {
        if (showCamera && cameraRef.current) {
            navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } })
                .then(stream => {
                    cameraRef.current.srcObject = stream;
                })
                .catch(err => {
                    console.error("Error accessing camera:", err);
                    setError("Could not access camera. Please check permissions.");
                });
        }
    }, [showCamera]);

    const saveToHistory = (newShapes) => {
        const newHistory = shapeHistory.slice(0, historyIndex + 1);
        newHistory.push([...newShapes]);
        setShapeHistory(newHistory);
        setHistoryIndex(newHistory.length - 1);
    };

    const handleUndo = () => {
        if (historyIndex > 0) {
            const newIndex = historyIndex - 1;
            setHistoryIndex(newIndex);
            setShapes([...shapeHistory[newIndex]]);
            redrawShapes();
        }
    };

    const handleRedo = () => {
        if (historyIndex < shapeHistory.length - 1) {
            const newIndex = historyIndex + 1;
            setHistoryIndex(newIndex);
            setShapes([...shapeHistory[newIndex]]);
            redrawShapes();
        }
    };

    // Restore File object from persistent preview if needed
    useEffect(() => {
        if (preview && preview.startsWith('data:image') && !selectedFile) {
            // Convert base64 to blob
            try {
                const base64Data = preview.split(',')[1];
                const binaryData = atob(base64Data);
                const arrayBuffer = new ArrayBuffer(binaryData.length);
                const uint8Array = new Uint8Array(arrayBuffer);
                for (let i = 0; i < binaryData.length; i++) {
                    uint8Array[i] = binaryData.charCodeAt(i);
                }
                const blob = new Blob([uint8Array], { type: 'image/png' });
                const file = new File([blob], `draft_photo_${Date.now()}.png`, { type: 'image/png' });
                setSelectedFile(file);
            } catch (e) {
                console.error("Error restoring draft file:", e);
            }
        }
    }, [preview, selectedFile]);

    // Initialize canvas when preview changes
    useEffect(() => {
        if (preview && canvasRef.current) {
            const canvas = canvasRef.current;
            const ctx = canvas.getContext('2d');
            const img = new Image();
            
            img.onload = () => {
                // Set canvas to image dimensions
                canvas.width = img.width;
                canvas.height = img.height;
                
                // Clear and draw image
                ctx.clearRect(0, 0, canvas.width, canvas.height);
                ctx.drawImage(img, 0, 0);
                
                // Redraw all shapes
                redrawShapes();
            };
            
            img.src = preview;
        }
    }, [preview]);

    // Redraw all shapes on canvas
    const redrawShapes = () => {
        if (!preview || !canvasRef.current) return;
        
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const img = new Image();
        
        img.onload = () => {
            // Clear and redraw image
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(img, 0, 0);
            
            // Draw all saved shapes
            shapes.forEach(shape => {
                drawShape(ctx, shape);
            });
            
            // Draw current shape being drawn (if any)
            if (currentShape && currentShape.type !== SHAPE_TYPES.TEXT && currentShape.type !== SHAPE_TYPES.FREEHAND) {
                drawShape(ctx, currentShape);
            }
            
            // Draw freehand points if in freehand mode
            if (freehandPoints.length > 0) {
                drawFreehand(ctx, freehandPoints);
            }
        };
        
        img.src = preview;
    };

    const drawShape = (ctx, shape) => {
        ctx.save();
        ctx.strokeStyle = shape.color;
        ctx.fillStyle = shape.color;
        ctx.lineWidth = shape.size;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        
        switch(shape.type) {
            case SHAPE_TYPES.RECTANGLE:
                ctx.strokeRect(shape.x, shape.y, shape.width, shape.height);
                break;
            case SHAPE_TYPES.SQUARE:
                const squareSize = Math.max(Math.abs(shape.width), Math.abs(shape.height));
                const squareX = shape.width < 0 ? shape.x - squareSize : shape.x;
                const squareY = shape.height < 0 ? shape.y - squareSize : shape.y;
                ctx.strokeRect(squareX, squareY, squareSize, squareSize);
                break;
            case SHAPE_TYPES.CIRCLE:
                ctx.beginPath();
                ctx.arc(shape.x, shape.y, shape.radius, 0, Math.PI * 2);
                ctx.stroke();
                break;
            case SHAPE_TYPES.LINE:
                ctx.beginPath();
                ctx.moveTo(shape.x1, shape.y1);
                ctx.lineTo(shape.x2, shape.y2);
                ctx.stroke();
                break;
            case SHAPE_TYPES.ARROW:
                drawArrow(ctx, shape);
                break;
            case SHAPE_TYPES.TEXT:
                ctx.font = `${shape.size * 10}px Arial`;
                ctx.fillStyle = shape.color;
                ctx.textBaseline = 'top';
                ctx.fillText(shape.text, shape.x, shape.y);
                break;
            case SHAPE_TYPES.FREEHAND:
                if (shape.points && shape.points.length > 1) {
                    ctx.beginPath();
                    ctx.moveTo(shape.points[0].x, shape.points[0].y);
                    for (let i = 1; i < shape.points.length; i++) {
                        ctx.lineTo(shape.points[i].x, shape.points[i].y);
                    }
                    ctx.stroke();
                }
                break;
        }
        ctx.restore();
    };

    const drawFreehand = (ctx, points) => {
        if (points.length < 2) return;
        
        ctx.save();
        ctx.strokeStyle = brushColor;
        ctx.lineWidth = brushSize;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        
        ctx.beginPath();
        ctx.moveTo(points[0].x, points[0].y);
        for (let i = 1; i < points.length; i++) {
            ctx.lineTo(points[i].x, points[i].y);
        }
        ctx.stroke();
        ctx.restore();
    };

    const drawArrow = (ctx, shape) => {
        const headLength = 15;
        const angle = Math.atan2(shape.y2 - shape.y1, shape.x2 - shape.x1);
        
        // Draw line
        ctx.beginPath();
        ctx.moveTo(shape.x1, shape.y1);
        ctx.lineTo(shape.x2, shape.y2);
        ctx.stroke();
        
        // Draw arrow head
        ctx.beginPath();
        ctx.moveTo(shape.x2, shape.y2);
        ctx.lineTo(
            shape.x2 - headLength * Math.cos(angle - Math.PI / 6),
            shape.y2 - headLength * Math.sin(angle - Math.PI / 6)
        );
        ctx.lineTo(
            shape.x2 - headLength * Math.cos(angle + Math.PI / 6),
            shape.y2 - headLength * Math.sin(angle + Math.PI / 6)
        );
        ctx.closePath();
        ctx.fill();
    };

    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (file) {
            setSelectedFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setPreview(reader.result);
            };
            reader.readAsDataURL(file);
            setError("");
        }
    };

    const capturePhoto = () => {
        if (cameraRef.current) {
            const canvas = document.createElement('canvas');
            canvas.width = cameraRef.current.videoWidth;
            canvas.height = cameraRef.current.videoHeight;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(cameraRef.current, 0, 0);
            
            canvas.toBlob((blob) => {
                const file = new File([blob], `photo_${Date.now()}.jpg`, { type: 'image/jpeg' });
                setSelectedFile(file);
                setPreview(canvas.toDataURL('image/jpeg'));
                setShowCamera(false);
                
                // Stop camera stream
                const stream = cameraRef.current.srcObject;
                if (stream) {
                    stream.getTracks().forEach(track => track.stop());
                }
            }, 'image/jpeg');
        }
    };

    const getMousePos = (event) => {
        if (!canvasRef.current) return { x: 0, y: 0 };
        const rect = canvasRef.current.getBoundingClientRect();
        const scaleX = canvasRef.current.width / rect.width;
        const scaleY = canvasRef.current.height / rect.height;
        return {
            x: (event.clientX - rect.left) * scaleX,
            y: (event.clientY - rect.top) * scaleY
        };
    };

    const handleMouseDown = (event) => {
        if (!drawingMode || !preview) return;
        
        const pos = getMousePos(event);
        startPosRef.current = pos;
        
        if (drawingMode === SHAPE_TYPES.TEXT) {
            setTextPosition(pos);
            setShowTextModal(true);
            return;
        }
        
        setIsDrawing(true);
        
        if (drawingMode === SHAPE_TYPES.FREEHAND) {
            setFreehandPoints([pos]);
        } else {
            switch(drawingMode) {
                case SHAPE_TYPES.RECTANGLE:
                    setCurrentShape({
                        type: SHAPE_TYPES.RECTANGLE,
                        x: pos.x,
                        y: pos.y,
                        width: 0,
                        height: 0,
                        color: brushColor,
                        size: brushSize
                    });
                    break;
                case SHAPE_TYPES.SQUARE:
                    setCurrentShape({
                        type: SHAPE_TYPES.SQUARE,
                        x: pos.x,
                        y: pos.y,
                        width: 0,
                        height: 0,
                        color: brushColor,
                        size: brushSize
                    });
                    break;
                case SHAPE_TYPES.CIRCLE:
                    setCurrentShape({
                        type: SHAPE_TYPES.CIRCLE,
                        x: pos.x,
                        y: pos.y,
                        radius: 0,
                        color: brushColor,
                        size: brushSize
                    });
                    break;
                case SHAPE_TYPES.LINE:
                case SHAPE_TYPES.ARROW:
                    setCurrentShape({
                        type: drawingMode,
                        x1: pos.x,
                        y1: pos.y,
                        x2: pos.x,
                        y2: pos.y,
                        color: brushColor,
                        size: brushSize
                    });
                    break;
            }
        }
    };

    const handleMouseMove = (event) => {
        if (!isDrawing || !preview) return;
        
        const pos = getMousePos(event);
        
        if (drawingMode === SHAPE_TYPES.FREEHAND) {
            setFreehandPoints(prev => [...prev, pos]);
            redrawShapes();
            return;
        }
        
        if (!currentShape) return;
        
        switch(currentShape.type) {
            case SHAPE_TYPES.RECTANGLE:
                setCurrentShape({
                    ...currentShape,
                    width: pos.x - currentShape.x,
                    height: pos.y - currentShape.y
                });
                break;
            case SHAPE_TYPES.SQUARE:
                const size = Math.max(Math.abs(pos.x - currentShape.x), Math.abs(pos.y - currentShape.y));
                setCurrentShape({
                    ...currentShape,
                    width: pos.x > currentShape.x ? size : -size,
                    height: pos.y > currentShape.y ? size : -size
                });
                break;
            case SHAPE_TYPES.CIRCLE:
                const radius = Math.sqrt(
                    Math.pow(pos.x - currentShape.x, 2) + 
                    Math.pow(pos.y - currentShape.y, 2)
                );
                setCurrentShape({
                    ...currentShape,
                    radius: radius
                });
                break;
            case SHAPE_TYPES.LINE:
            case SHAPE_TYPES.ARROW:
                setCurrentShape({
                    ...currentShape,
                    x2: pos.x,
                    y2: pos.y
                });
                break;
        }
        
        redrawShapes();
    };

    const handleMouseUp = () => {
        if (!isDrawing || !preview) return;
        
        if (drawingMode === SHAPE_TYPES.FREEHAND) {
            if (freehandPoints.length > 1) {
                const newShape = {
                    type: SHAPE_TYPES.FREEHAND,
                    points: [...freehandPoints],
                    color: brushColor,
                    size: brushSize
                };
                const newShapes = [...shapes, newShape];
                setShapes(newShapes);
                saveToHistory(newShapes);
            }
            setFreehandPoints([]);
        } else if (currentShape) {
            // Check if shape has meaningful size
            const hasSize = 
                (currentShape.type === SHAPE_TYPES.RECTANGLE && Math.abs(currentShape.width) > 5 && Math.abs(currentShape.height) > 5) ||
                (currentShape.type === SHAPE_TYPES.SQUARE && (Math.abs(currentShape.width) > 5 || Math.abs(currentShape.height) > 5)) ||
                (currentShape.type === SHAPE_TYPES.CIRCLE && currentShape.radius > 5) ||
                (currentShape.type === SHAPE_TYPES.LINE && 
                    Math.abs(currentShape.x2 - currentShape.x1) > 5 && 
                    Math.abs(currentShape.y2 - currentShape.y1) > 5) ||
                (currentShape.type === SHAPE_TYPES.ARROW && 
                    Math.abs(currentShape.x2 - currentShape.x1) > 5 && 
                    Math.abs(currentShape.y2 - currentShape.y1) > 5);
            
            if (hasSize) {
                const newShapes = [...shapes, currentShape];
                setShapes(newShapes);
                saveToHistory(newShapes);
            }
        }
        
        setCurrentShape(null);
        setIsDrawing(false);
        redrawShapes();
    };

    const handleTextSubmit = () => {
        if (textInput.trim()) {
            const newTextShape = {
                type: SHAPE_TYPES.TEXT,
                x: textPosition.x,
                y: textPosition.y,
                text: textInput,
                color: brushColor,
                size: brushSize
            };
            
            const newShapes = [...shapes, newTextShape];
            setShapes(newShapes);
            saveToHistory(newShapes);
            setTextInput("");
            setShowTextModal(false);
            redrawShapes();
        }
    };

    const handleSave = () => {
        if (!selectedFile) {
            setError("Please select or capture a photo first.");
            return;
        }
        
        if (!title.trim()) {
            setError("Please enter a title/description for the photo.");
            return;
        }
        
        // Get final canvas with all shapes
        const canvas = canvasRef.current;
        if (!canvas) return;
        
        const finalImage = canvas.toDataURL('image/png');
        
        canvas.toBlob((blob) => {
            const file = new File([blob], selectedFile.name || `photo_${Date.now()}.png`, { type: 'image/png' });
            
            onSave({
                file: file,
                title: title.trim(),
                shapes: shapes,
                imageUrl: finalImage
            });
            
            // Clear draft on successful save
            if (persistenceKey) {
                clearDraft();
            }

            // Reset form
            setSelectedFile(null);
            if (!persistenceKey) {
                setPreview(null);
                setTitle("");
                setShapes([]);
            }
            setShapeHistory([[]]);
            setHistoryIndex(0);
            setCurrentShape(null);
            setTextInput("");
            setShowTextModal(false);
            setError("");
        }, 'image/png');
    };

    const handleClearAllShapes = () => {
        setShapes([]);
        saveToHistory([]);
        redrawShapes();
    };

    // Cleanup camera on unmount
    useEffect(() => {
        return () => {
            if (cameraRef.current && cameraRef.current.srcObject) {
                const stream = cameraRef.current.srcObject;
                stream.getTracks().forEach(track => track.stop());
            }
        };
    }, []);

    // Redraw shapes when they change
    useEffect(() => {
        if (preview) {
            redrawShapes();
        }
    }, [shapes, preview]);

    return (
        <>
            <Dialog 
                open={open} 
                onClose={onClose} 
                maxWidth="xl" 
                fullWidth 
                fullScreen
                sx={{ 
                    '& .MuiDialog-paper': { 
                        margin: 0,
                        width: '100%',
                        maxWidth: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        backgroundColor: '#f0f0f0'
                    } 
                }}
            >
                {/* HEADER - Drawing Tools Like Paint */}
                <DialogTitle sx={{ 
                    pb: 1, 
                    pt: 1, 
                    backgroundColor: '#ffffff',
                    borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                    flexShrink: 0,
                    position: 'sticky',
                    top: 0,
                    zIndex: 10
                }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography sx={{ ...TYPOGRAPHY.H3, color: COLORS.PRIMARY }}>
                            Photo Annotation Tool
                        </Typography>
                        
                        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                            {/* Undo/Redo Buttons - Symbols only */}
                            <Tooltip title="Undo (Ctrl+Z)">
                                <IconButton
                                    onClick={handleUndo}
                                    disabled={historyIndex === 0}
                                    sx={{ 
                                        borderRadius: 1,
                                        bgcolor: historyIndex === 0 ? 'transparent' : COLORS.LIGHT_SECONDARY + '20',
                                        '&:hover': { bgcolor: COLORS.LIGHT_SECONDARY + '40' }
                                    }}
                                >
                                    <Icon name="undo"  />
                                </IconButton>
                            </Tooltip>
                            
                            <Tooltip title="Redo (Ctrl+Y)">
                                <IconButton
                                    onClick={handleRedo}
                                    disabled={historyIndex === shapeHistory.length - 1}
                                    sx={{ 
                                        borderRadius: 1,
                                        bgcolor: historyIndex === shapeHistory.length - 1 ? 'transparent' : COLORS.LIGHT_SECONDARY + '20',
                                        '&:hover': { bgcolor: COLORS.LIGHT_SECONDARY + '40' }
                                    }}
                                >
                                    <Icon name="redo"  />
                                </IconButton>
                            </Tooltip>
                            
                            <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                            
                            {/* Photo Selection Buttons */}
                            <input
                                accept="image/*"
                                style={{ display: 'none' }}
                                id="photo-upload"
                                type="file"
                                onChange={handleFileSelect}
                            />
                            <Tooltip title="Select Photo from Device">
                                <Button
                                    component="label"
                                    htmlFor="photo-upload"
                                    variant="outlined"
                                    size="small"
                                    startIcon={<Icon name="gallery" />}
                                    sx={{ 
                                        borderRadius: 1,
                                        textTransform: 'none'
                                    }}
                                >
                                    Select Photo
                                </Button>
                            </Tooltip>
                            
                            <Tooltip title="Capture Photo from Camera">
                                <Button
                                    onClick={() => setShowCamera(true)}
                                    variant="outlined"
                                    size="small"
                                    startIcon={<Icon name="camera" />}
                                    sx={{ 
                                        borderRadius: 1,
                                        textTransform: 'none'
                                    }}
                                >
                                    Capture Photo
                                </Button>
                            </Tooltip>
                        </Box>
                    </Box>
                    
                    {/* Drawing Tools Row */}
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1, 
                        mt: 1,
                        flexWrap: 'wrap'
                    }}>
                        <Typography sx={{ ...TYPOGRAPHY.BODY_SMALL, color: COLORS.TEXT, mr: 1 }}>
                            Drawing Tools:
                        </Typography>
                        
                        <ToggleButtonGroup
                            value={drawingMode}
                            exclusive
                            onChange={(e, newMode) => newMode && setDrawingMode(newMode)}
                            size="small"
                            sx={{ 
                                '& .MuiToggleButton-root': {
                                    border: '1px solid',
                                    borderColor: COLORS.LIGHT_SECONDARY,
                                    px: 1.5,
                                    py: 0.5,
                                    minWidth: 'auto',
                                    '&.Mui-selected': {
                                        bgcolor: COLORS.SECONDARY + '20',
                                        borderColor: COLORS.SECONDARY,
                                        color: COLORS.SECONDARY
                                    }
                                }
                            }}
                        >
                            <ToggleButton value={SHAPE_TYPES.RECTANGLE} title="Rectangle">
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                    <Icon name="square" size={18} />
                                    <Typography variant="caption" sx={{ fontSize: '0.6rem', mt: 0.2 }}>
                                        Rectangle
                                    </Typography>
                                </Box>
                            </ToggleButton>
                            {/* <ToggleButton value={SHAPE_TYPES.SQUARE} title="Square">
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                    <Icon name="squareFull" size={18} />
                                    <Typography variant="caption" sx={{ fontSize: '0.6rem', mt: 0.2 }}>
                                        Square
                                    </Typography>
                                </Box>
                            </ToggleButton> */}
                            <ToggleButton value={SHAPE_TYPES.CIRCLE} title="Circle">
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                    <Icon name="circle" size={18} />
                                    <Typography variant="caption" sx={{ fontSize: '0.6rem', mt: 0.2 }}>
                                        Circle
                                    </Typography>
                                </Box>
                            </ToggleButton>
                            <ToggleButton value={SHAPE_TYPES.LINE} title="Line">
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                    <Icon name="line" size={18} />
                                    <Typography variant="caption" sx={{ fontSize: '0.6rem', mt: 0.2 }}>
                                        Line
                                    </Typography>
                                </Box>
                            </ToggleButton>
                            <ToggleButton value={SHAPE_TYPES.ARROW} title="Arrow">
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                             
                                    <Typography variant="caption" sx={{ fontSize: '0.6rem', mt: 0.2 }}>
                                        Arrow
                                    </Typography>
                                </Box>
                            </ToggleButton>
                            {/* <ToggleButton value={SHAPE_TYPES.FREEHAND} title="Freehand">
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                    <Icon name="pen" size={18} />
                                    <Typography variant="caption" sx={{ fontSize: '0.6rem', mt: 0.2 }}>
                                        Freehand
                                    </Typography>
                                </Box>
                            </ToggleButton> */}
                            <ToggleButton value={SHAPE_TYPES.TEXT} title="Text">
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                    <Icon name="text" size={18} />
                                    <Typography variant="caption" sx={{ fontSize: '0.6rem', mt: 0.2 }}>
                                        Text
                                    </Typography>
                                </Box>
                            </ToggleButton>
                        </ToggleButtonGroup>
                        
                        <Divider orientation="vertical" flexItem sx={{ mx: 1, height: 30 }} />
                        
                        {/* Brush Size */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography sx={{ ...TYPOGRAPHY.BODY_SMALL, color: COLORS.TEXT }}>
                                Size:
                            </Typography>
                            <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center',
                                gap: 0.5 
                            }}>
                                {[1, 3, 5, 8, 12].map((size) => (
                                    <Tooltip key={size} title={`${size}px`}>
                                        <IconButton
                                            size="small"
                                            onClick={() => setBrushSize(size)}
                                            sx={{
                                                width: 32,
                                                height: 32,
                                                bgcolor: brushSize === size ? COLORS.SECONDARY + '20' : 'transparent',
                                                border: `1px solid ${brushSize === size ? COLORS.SECONDARY : COLORS.LIGHT_SECONDARY}`,
                                                '&:hover': {
                                                    bgcolor: COLORS.LIGHT_SECONDARY + '20'
                                                }
                                            }}
                                        >
                                            <Box sx={{ 
                                                width: size * 2,
                                                height: size * 2,
                                                bgcolor: brushColor,
                                                borderRadius: '50%'
                                            }} />
                                        </IconButton>
                                    </Tooltip>
                                ))}
                            </Box>
                        </Box>
                        
                        <Divider orientation="vertical" flexItem sx={{ mx: 1, height: 30 }} />
                        
                        {/* Color Palette */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography sx={{ ...TYPOGRAPHY.BODY_SMALL, color: COLORS.TEXT, mr: 0.5 }}>
                                Colors:
                            </Typography>
                            {[
                                '#ff0000', '#ff9900', '#ffff00', '#00ff00', 
                                '#00ffff', '#0000ff', '#9900ff', '#ff00ff',
                                '#000000', '#666666', '#999999', '#ffffff'
                            ].map((color) => (
                                <Tooltip key={color} title={color}>
                                    <IconButton
                                        size="small"
                                        onClick={() => setBrushColor(color)}
                                        sx={{
                                            width: 24,
                                            height: 24,
                                            bgcolor: color,
                                            border: brushColor === color ? `2px solid ${COLORS.PRIMARY}` : 
                                                   color === '#ffffff' ? `1px solid ${COLORS.LIGHT_SECONDARY}` : 'none',
                                            '&:hover': { 
                                                bgcolor: color,
                                                border: `2px solid ${COLORS.PRIMARY}`
                                            }
                                        }}
                                    />
                                </Tooltip>
                            ))}
                        </Box>
                        
                        <Divider orientation="vertical" flexItem sx={{ mx: 1, height: 30 }} />
                        
                        {/* Clear All Button */}
                        <Tooltip title="Clear All Shapes">
                            <Button
                                onClick={handleClearAllShapes}
                                variant="outlined"
                                size="small"
                                startIcon={<Icon name="delete" />}
                                sx={{ 
                                    color: COLORS.ERROR,
                                    borderColor: COLORS.ERROR,
                                    '&:hover': { 
                                        bgcolor: COLORS.ERROR + '10',
                                        borderColor: COLORS.ERROR
                                    }
                                }}
                            >
                                Clear All
                            </Button>
                        </Tooltip>
                    </Box>
                </DialogTitle>
                
                <DialogContent sx={{ 
                    p: 0, 
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    backgroundColor: '#2c2c2c'
                }}>
                    {/* MAIN DRAWING AREA - FULL SCREEN */}
                    <Box sx={{ 
                        flex: 1,
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'auto',
                        padding: '0 !important' // Remove padding to maximize space
                    }}>
                        {/* Camera View */}
                        {showCamera && (
                            <Box sx={{ 
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                backgroundColor: '#000',
                                zIndex: 10,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                p: 2
                            }}>
                                <Typography sx={{ ...TYPOGRAPHY.H4, color: '#fff', mb: 2, textAlign: 'center' }}>
                                    Camera Preview
                                </Typography>
                                
                                <Box sx={{ 
                                    position: 'relative',
                                    width: '100%',
                                    maxWidth: 800,
                                    maxHeight: '60%',
                                    borderRadius: 2,
                                    overflow: 'hidden',
                                    border: '2px solid #fff',
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.5)'
                                }}>
                                    <video
                                        ref={cameraRef}
                                        autoPlay
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                            display: 'block'
                                        }}
                                    />
                                </Box>
                                
                                <Box sx={{ 
                                    mt: 3, 
                                    display: 'flex', 
                                    gap: 2,
                                    alignItems: 'center'
                                }}>
                                    <Button
                                        variant="contained"
                                        onClick={capturePhoto}
                                        startIcon={<Icon name="camera" size={20} />}
                                        sx={{ 
                                            bgcolor: COLORS.SECONDARY,
                                            px: 4,
                                            py: 1.5,
                                            fontSize: '1rem'
                                        }}
                                    >
                                        Capture Photo
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        onClick={() => setShowCamera(false)}
                                        color="inherit"
                                        sx={{ 
                                            color: '#fff',
                                            borderColor: '#fff',
                                            '&:hover': {
                                                borderColor: '#fff',
                                                bgcolor: 'rgba(255,255,255,0.1)'
                                            }
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                </Box>
                            </Box>
                        )}
                        
                        {/* No Photo Selected */}
                        {!preview && !showCamera && (
                            <Box sx={{ 
                                display: 'flex', 
                                flexDirection: 'column',
                                alignItems: 'center', 
                                justifyContent: 'center',
                                color: '#fff',
                                textAlign: 'center',
                                p: 3,
                                maxWidth: 600
                            }}>
                                <Icon name="camera" size={120} color={COLORS.LIGHT_SECONDARY} sx={{ mb: 3, opacity: 0.5 }} />
                                <Typography sx={{ ...TYPOGRAPHY.H4, color: '#fff', mb: 2, opacity: 0.8 }}>
                                    Select or Capture a Photo
                                </Typography>
                                <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.LIGHT_SECONDARY, opacity: 0.6, mb: 4 }}>
                                    Use the "Select Photo" or "Capture Photo" buttons in the toolbar above to begin annotating
                                </Typography>
                                
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <input
                                        accept="image/*"
                                        style={{ display: 'none' }}
                                        id="photo-upload-main"
                                        type="file"
                                        onChange={handleFileSelect}
                                    />
                                    <Button
                                        component="label"
                                        htmlFor="photo-upload-main"
                                        variant="contained"
                                        size="large"
                                        startIcon={<Icon name="gallery" />}
                                        sx={{ 
                                            bgcolor: COLORS.SECONDARY,
                                            px: 4,
                                            py: 1.5
                                        }}
                                    >
                                        Select from Device
                                    </Button>
                                    
                                    <Button
                                        onClick={() => setShowCamera(true)}
                                        variant="contained"
                                        size="large"
                                        startIcon={<Icon name="camera" />}
                                        sx={{ 
                                            bgcolor: COLORS.PRIMARY,
                                            px: 4,
                                            py: 1.5
                                        }}
                                    >
                                        Use Camera
                                    </Button>
                                </Box>
                            </Box>
                        )}
                        
                        {/* Canvas with Photo */}
                        {preview && !showCamera && (
                            <Box sx={{ 
                                position: 'relative',
                                width: '100%',
                                height: '100%',
                                cursor: drawingMode ? 'crosshair' : 'default',
                                overflow: 'auto',
                                backgroundColor: '#000',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <canvas
                                    ref={canvasRef}
                                    onMouseDown={handleMouseDown}
                                    onMouseMove={handleMouseMove}
                                    onMouseUp={handleMouseUp}
                                    onMouseLeave={handleMouseUp}
                                    style={{
                                        display: 'block',
                                        maxWidth: '100%',
                                        maxHeight: '100%',
                                        objectFit: 'contain'
                                    }}
                                />
                                
                                {/* Drawing Mode Indicator */}
                                {drawingMode && !showTextModal && (
                                    <Paper sx={{
                                        position: 'absolute',
                                        top: 10,
                                        left: 10,
                                        bgcolor: 'rgba(0,0,0,0.8)',
                                        color: '#fff',
                                        p: 1,
                                        borderRadius: 1,
                                        ...TYPOGRAPHY.BODY_SMALL,
                                        fontSize: '0.75rem',
                                        zIndex: 5,
                                        border: `1px solid ${brushColor}`,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5
                                    }}>
                                        <Box sx={{ 
                                            width: 12, 
                                            height: 12, 
                                            bgcolor: brushColor,
                                            borderRadius: drawingMode === SHAPE_TYPES.CIRCLE ? '50%' : '0'
                                        }} />
                                        {drawingMode === SHAPE_TYPES.TEXT ? 
                                            'Click to add text' : 
                                            `Click and drag to draw ${drawingMode}`}
                                    </Paper>
                                )}
                            </Box>
                        )}
                    </Box>
                </DialogContent>
                
                {/* BOTTOM SECTION - Title/Description + Save/Cancel Buttons */}
                <DialogActions sx={{ 
                    p: 2, 
                    pt: 1, 
                    pb: 1.5,
                    borderTop: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                    backgroundColor: '#ffffff',
                    flexShrink: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch'
                }}>
                    {error && (
                        <Alert severity="error" sx={{ mb: 1.5, width: '100%' }}>
                            {error}
                        </Alert>
                    )}
                    
                    <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between',
                        alignItems: 'flex-start', 
                        gap: 3,
                        width: '100%'
                    }}>
                        {/* Title/Description Field - Left Side */}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.TEXT, mb: 0.5 }}>
                                Photo Title / Description *
                            </Typography>
                            
                            <TextField
                                fullWidth
                                placeholder="Enter a descriptive title for this photo..."
                                value={title}
                                onChange={(e) => {
                                    setTitle(e.target.value);
                                    setError("");
                                }}
                                size="small"
                                required
                                error={!title.trim()}
                               //elperText={!title.trim() ? "Title/Description is required" : ""}
                                sx={{ 
                                    '& .MuiOutlinedInput-root': {
                                        backgroundColor: '#f8f8f8',
                                        fontSize: '0.875rem'
                                    }
                                }}
                            />
                        </Box>
                        
                        {/* File Info & Shape Count - Middle */}
                        {/* <Box sx={{ 
                            display: 'flex', 
                            flexDirection: 'column',
                            justifyContent: 'flex-start',
                            alignItems: 'flex-start',
                            minWidth: 200,
                            pt: 0.5
                        }}>
                            {selectedFile && (
                                <Typography sx={{ 
                                    ...TYPOGRAPHY.BODY_SMALL, 
                                    color: COLORS.SUCCESS,
                                    mb: 0.5
                                }}>
                                    ✓ {selectedFile.name}
                                </Typography>
                            )}
                            
                            <Typography sx={{ 
                                ...TYPOGRAPHY.BODY_SMALL, 
                                color: COLORS.LIGHT_SECONDARY
                            }}>
                                {shapes.length} annotation{shapes.length !== 1 ? 's' : ''} added
                            </Typography>
                        </Box> */}
                        
                        {/* Save/Cancel Buttons - Right Side */}
                        <Box sx={{ 
                            display: 'flex', 
                            gap: 2,
                            alignItems: 'center',
                          pt:2.5
                        }}>
                            {/* <Typography sx={{ ...TYPOGRAPHY.BODY_SMALL, color: COLORS.LIGHT_SECONDARY, mr: 1 }}>
                                * Required
                            </Typography>
                             */}
                            <Button 
                                onClick={onClose} 
                                variant="outlined"
                                sx={{ minWidth: 90 }}
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleSave}
                                variant="contained"
                                disabled={!selectedFile || !title.trim()}
                                sx={{ 
                                    bgcolor: COLORS.SECONDARY,
                                    minWidth: 120,
                                    '&:disabled': {
                                        bgcolor: COLORS.LIGHT_SECONDARY
                                    }
                                }}
                                startIcon={<Icon name="check" />}
                            >
                                Save Photo
                            </Button>
                        </Box>
                    </Box>
                </DialogActions>
            </Dialog>

            {/* Text Input Modal */}
            <Modal
                open={showTextModal}
                onClose={() => {
                    setShowTextModal(false);
                    setTextInput("");
                }}
                closeAfterTransition
                BackdropComponent={Backdrop}
                BackdropProps={{
                    timeout: 500,
                }}
            >
                <Fade in={showTextModal}>
                    <Box sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 400,
                        bgcolor: 'background.paper',
                        borderRadius: 2,
                        boxShadow: 24,
                        p: 3,
                        outline: 'none'
                    }}>
                        <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.PRIMARY, mb: 3 }}>
                            Add Text Annotation
                        </Typography>
                        
                        <TextField
                            autoFocus
                            fullWidth
                            multiline
                            rows={3}
                            value={textInput}
                            onChange={(e) => setTextInput(e.target.value)}
                            placeholder="Enter text to add to the image..."
                            sx={{ mb: 3 }}
                            onKeyPress={(e) => {
                                if (e.key === 'Enter' && e.ctrlKey) {
                                    handleTextSubmit();
                                }
                            }}
                        />
                        
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
    <Typography sx={{ ...TYPOGRAPHY.BODY_SMALL, mr: 1 }}>
        Size:
    </Typography>
    <Box sx={{ display: 'flex', gap: 0.5 }}>
        {[1, 2, 3, 4, 5].map((size) => (
            <IconButton
                key={size}
                onClick={() => setBrushSize(size)}
                size="small"
                sx={{
                    width: 32, // Fixed width
                    height: 32, // Fixed height - SAME as width for circle
                    bgcolor: brushSize === size ? COLORS.SECONDARY + '20' : 'transparent',
                    border: `1px solid ${brushSize === size ? COLORS.SECONDARY : COLORS.LIGHT_SECONDARY}`,
                    borderRadius: '50%', // This makes it a perfect circle
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    '&:hover': {
                        bgcolor: brushSize === size ? COLORS.SECONDARY + '30' : COLORS.LIGHT_SECONDARY + '20'
                    }
                }}
            >
                <Typography 
                    sx={{ 
                        fontSize: size * 4 + 10,
                        fontWeight: brushSize === size ? 'bold' : 'normal',
                        color: brushSize === size ? COLORS.SECONDARY : COLORS.TEXT
                    }}
                >
                    A
                </Typography>
            </IconButton>
        ))}
    </Box>
</Box>
                            
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                {['#ff0000', '#0000ff', '#000000', '#ffffff'].map((color) => (
                                    <IconButton
                                        key={color}
                                        onClick={() => setBrushColor(color)}
                                        sx={{
                                            width: 24,
                                            height: 24,
                                            bgcolor: color,
                                            border: brushColor === color ? `2px solid ${COLORS.PRIMARY}` : 
                                                   color === '#ffffff' ? `1px solid ${COLORS.LIGHT_SECONDARY}` : 'none',
                                        }}
                                    />
                                ))}
                            </Box>
                        </Box>
                        
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                            <Button
                                onClick={() => {
                                    setShowTextModal(false);
                                    setTextInput("");
                                }}
                                variant="outlined"
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleTextSubmit}
                                variant="contained"
                                disabled={!textInput.trim()}
                                sx={{ bgcolor: COLORS.SECONDARY }}
                            >
                                Add to Image
                            </Button>
                        </Box>
                    </Box>
                </Fade>
            </Modal>
        </>
    );
};

export default PhotoUploadWithDrawing;